import { calculateWorkOrderCosts } from '@calculations/cost/work-order';
import { calculate } from '@calculations/price/strategies/target-gross-margin/calculate';
import { calculateContributionMarginFromUnitPrice } from '@calculations/price/utilities/contribution-margin';
import { calculateUnitPriceFromGrossMargin } from '@calculations/price/utilities/gross-margin';
import { calculateMarginsFromUnitPrice } from '@calculations/price/utilities/margins';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategy } from '@snap-types/models/enums';
import { Decimal } from '@snap-types/types/decimal';

import { storeToCalculationSnapWorkOrder } from '../../transforms/store-to-calculation-snap-work-order';
import { transformWorkOrderCostsToStoreState } from '../../utilities/transform-work-order-costs';
import {
  OnGrossMarginChangeInput,
  OnGrossMarginChangeOutput,
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  OnUnitPriceChangeInput,
  OnUnitPriceChangeOutput,
  RecalculatePriceStrategy,
} from '../types';

function onQuantityChange({
  state,
  quoteLine,
  quotePrice,
  quantity,
  priceStrategyConfigs,
}: OnQuantityChangeInput): OnQuantityChangeOutput {
  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  const priceStrategyConfig = priceStrategyConfigs.find(
    (config) => config.priceStrategyType === quotePrice.priceStrategy,
  );

  if (
    !priceStrategyConfig ||
    (priceStrategyConfig.priceStrategyType !==
      PriceStrategy.TargetGrossMargin &&
      priceStrategyConfig.priceStrategyType !==
        PriceStrategy.RAFTargetGrossMargin &&
      priceStrategyConfig.priceStrategyType !== PriceStrategy.NewPart)
  ) {
    return {
      status: 'failure',
      reason: 'No price strategy config found',
    };
  }

  const initialGrossMargin =
    quotePrice.grossMargin ?? priceStrategyConfig.targetGrossMargin;

  const costs = calculateWorkOrderCosts({
    quantity,
    ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
  });

  let targetGrossMargin =
    quotePrice.grossMargin || priceStrategyConfig.targetGrossMargin;

  const usingCalculatedGrossMargin =
    quotePrice.calculatedGrossMargin &&
    initialGrossMargin.eq(quotePrice.calculatedGrossMargin);

  if (usingCalculatedGrossMargin && priceStrategyConfig.targetGrossMargin) {
    targetGrossMargin = priceStrategyConfig.targetGrossMargin;
  }

  const lastWin = quoteLine.lastWin.hasLastWin
    ? {
        grossMargin: quoteLine.lastWin.grossMargin as Decimal,
        quantity: quoteLine.lastWin.quantity as Decimal,
        unitCost: quoteLine.lastWin.unitCost as Decimal,
        unitPrice: quoteLine.lastWin.unitPrice as Decimal,
      }
    : undefined;

  const { unitPrice, grossMargin } = calculate(
    {
      quantity,
      unitCost: costs.unitCost,
      targetGrossMargin,
      lastWin,
    },
    SnapRoundingOptionsCustom(state),
  );

  const contributionMargin = calculateContributionMarginFromUnitPrice({
    unitPrice,
    unitVariableCosts: costs.unitVariableCosts,
  });

  return {
    status: 'success',
    unitPrice,
    grossMargin,
    contributionMargin,
    ...transformWorkOrderCostsToStoreState(costs),
  };
}

function onUnitPriceChange({
  quotePrice,
  unitPrice,
}: OnUnitPriceChangeInput): OnUnitPriceChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No unit cost found',
    };
  }

  const margins = calculateMarginsFromUnitPrice({
    unitCost: quotePrice.unitCost,
    unitPrice,
    unitVariableCosts: quotePrice.unitVariableCosts,
  });

  return {
    status: 'success',
    ...margins,
  };
}

function onGrossMarginChange({
  state,
  quotePrice,
  grossMargin,
}: OnGrossMarginChangeInput): OnGrossMarginChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No unit cost found',
    };
  }

  const unitPrice = calculateUnitPriceFromGrossMargin(
    {
      unitCost: quotePrice.unitCost,
      grossMargin,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (quotePrice.unitVariableCosts) {
    const contributionMargin = calculateContributionMarginFromUnitPrice({
      unitVariableCosts: quotePrice.unitVariableCosts,
      unitPrice,
    });

    return {
      status: 'success',
      unitPrice,
      contributionMargin,
    };
  }

  return {
    status: 'success',
    unitPrice,
  };
}

function onQuotePriceCreation({
  state,
  quoteLine,
  quotePrice,
  priceStrategyConfigs,
}: OnQuotePriceCreationInput): OnQuotePriceCreationOutput {
  const { quantity } = quotePrice;

  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  const priceStrategyConfig = priceStrategyConfigs.find(
    (config) => config.priceStrategyType === quotePrice.priceStrategy,
  );

  if (
    !priceStrategyConfig ||
    (priceStrategyConfig.priceStrategyType !==
      PriceStrategy.TargetGrossMargin &&
      priceStrategyConfig.priceStrategyType !==
        PriceStrategy.RAFTargetGrossMargin &&
      priceStrategyConfig.priceStrategyType !== PriceStrategy.NewPart)
  ) {
    return {
      status: 'failure',
      reason: 'No price strategy config found',
    };
  }

  const targetGrossMargin =
    quotePrice.grossMargin ?? priceStrategyConfig.targetGrossMargin;

  const costs = calculateWorkOrderCosts({
    quantity,
    ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
  });

  const {
    hasLastWin,
    grossMargin: lastWinGrossMargin,
    unitCost: lastWinUnitCost,
    unitPrice: lastWinUnitPrice,
    quantity: lastWinQuantity,
  } = quoteLine.lastWin;

  const hasLastWinData =
    hasLastWin &&
    lastWinUnitCost &&
    lastWinGrossMargin &&
    lastWinUnitPrice &&
    lastWinQuantity;

  const lastWin = hasLastWinData
    ? {
        grossMargin: lastWinGrossMargin,
        quantity,
        unitCost: lastWinUnitCost,
        unitPrice: lastWinUnitPrice,
      }
    : undefined;

  const { unitPrice, grossMargin } = calculate(
    {
      quantity,
      unitCost: costs.unitCost,
      targetGrossMargin,
      lastWin,
    },
    SnapRoundingOptionsCustom(state),
  );

  const contributionMargin = calculateContributionMarginFromUnitPrice({
    unitPrice,
    unitVariableCosts: costs.unitVariableCosts,
  });

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice,
      grossMargin,
      contributionMargin,
      ...transformWorkOrderCostsToStoreState(costs),
    },
  };
}

export const recalculateTargetGrossMargin = {
  onQuantityChange,
  onUnitPriceChange,
  onGrossMarginChange,
  onContributionMarginChange: null,
  onBaseGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
